import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowLeft, FileText, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AdSlot } from '../components/ads/AdSlot';

const PageContent = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  // Get slug from URL path (e.g., /our-story -> our-story) or from params
  const slug = paramSlug || location.pathname.replace('/', '');
  
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [footerConfig, setFooterConfig] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const [pageRes, footerRes] = await Promise.all([
          axios.get(`${API}/page-content/${slug}`),
          axios.get(`${API}/footer-config`)
        ]);
        // Check if page is published
        if (pageRes.data.is_published === false) {
          setPage({ title: 'Page Not Available', content: '<p>This page is currently not available.</p>', is_draft: true });
        } else {
          setPage(pageRes.data);
        }
        setFooterConfig(footerRes.data);
      } catch (error) {
        console.error('Failed to fetch page:', error);
        setPage({ title: 'Page Not Found', content: '<p>This page does not exist or has not been published yet.</p>' });
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  const pageTitle = page?.title || slug?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-800">
              {footerConfig?.company_name || 'Officium Connect'}
            </span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">
              Sign In
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>

        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-sky-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800">{pageTitle}</h1>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg border p-8">
          {page?.content ? (
            <div 
              className="prose prose-slate max-w-none prose-headings:text-slate-800 prose-p:text-gray-600 prose-a:text-sky-600 prose-strong:text-slate-700"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">This page is coming soon.</p>
              <p className="text-sm mt-2">Content is being prepared by our team.</p>
            </div>
          )}
        </div>

        <AdSlot slot="content" enoughContent={(page?.content || '').length > 800} label="Sponsored" />
      </main>

      {/* Footer */}
      {footerConfig && (
        <footer className="bg-gray-50 border-t border-gray-200 mt-auto">
          <div className="max-w-5xl mx-auto px-6 py-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div>
                <h3 className="font-bold text-lg text-slate-800 mb-2">{footerConfig.company_name || 'Officium Connect'}</h3>
                <p className="text-gray-500 text-sm">
                  {footerConfig.company_tagline || "India's trusted platform for verified careers - Remote & Office."}
                </p>
              </div>

              {/* Company Links */}
              <div>
                <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider mb-3">COMPANY</h4>
                <ul className="space-y-2">
                  {(footerConfig.company_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.url} className="text-gray-500 hover:text-sky-600 transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support Links */}
              <div>
                <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider mb-3">SUPPORT</h4>
                <ul className="space-y-2">
                  {(footerConfig.support_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.url} className="text-gray-500 hover:text-sky-600 transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal Links */}
              <div>
                <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wider mb-3">LEGAL</h4>
                <ul className="space-y-2">
                  {(footerConfig.legal_links || []).filter(l => l.is_active !== false).map((link, idx) => (
                    <li key={idx}>
                      <Link to={link.url} className="text-gray-500 hover:text-sky-600 transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Bottom */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm">
                <p>{footerConfig.copyright_text || `© ${new Date().getFullYear()} Officium Connect. All rights reserved.`}</p>
                <p className="mt-1">{footerConfig.made_in_text || 'MADE WITH ❤️ IN INDIA'}</p>
              </div>
              <div className="flex gap-4">
                {footerConfig.social_instagram && (
                  <a href={footerConfig.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-600">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {footerConfig.social_facebook && (
                  <a href={footerConfig.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-600">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                )}
                {footerConfig.social_linkedin && (
                  <a href={footerConfig.social_linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-sky-600">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default PageContent;
